import React, { useEffect, useRef, useState } from "react";
import { Button, Icon, SpaceBetween } from "@amzn/awsui-components-react";
import { useAppDispatch, useAppSelector } from "app/store";
import { setAssistantOpen, setActiveTab } from "features/assistant/slice";
import { AssistantChatContent } from "../assistant-sidebar";
import { AnswersChatContent } from "../answers-chat-content";
import { useSelectedTeam } from "features/team-settings/hooks";
import SelectionLanding from "./SelectionLanding";
import styles from "./style.module.scss";

const MIN_WIDTH = 400;
const MAX_WIDTH_RATIO = 0.7;
const DEFAULT_WIDTH = 540;
const STORAGE_KEY = "gaia-sidebar-width";

const SidebarChatbotDrawer: React.FC = () => {
  const reduxDispatch = useAppDispatch();
  const isOpen = useAppSelector((s) => s.assistant.isOpen);
  const activeTab = useAppSelector((s) => s.assistant.activeTab);
  const pendingPrompt = useAppSelector((s) => s.assistant.pendingPrompt);
  const pendingAttachmentToken = useAppSelector(
    (s) => s.assistant.pendingAttachmentToken,
  );

  const { isAssistantEnabled, isAnswersKbEnabled } = useSelectedTeam();

  const [headerActions, setHeaderActions] = useState<React.ReactNode>(null);
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? Number.parseInt(stored, 10) : DEFAULT_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const widthRef = useRef(sidebarWidth);
  widthRef.current = sidebarWidth;

  useEffect(() => {
    if (pendingPrompt && isOpen && activeTab !== "assistant") {
      reduxDispatch(setActiveTab("assistant"));
    }
  }, [pendingPrompt, isOpen, activeTab, reduxDispatch]);

  useEffect(() => {
    if (pendingAttachmentToken && isOpen && activeTab !== "assistant") {
      reduxDispatch(setActiveTab("assistant"));
    }
  }, [pendingAttachmentToken, isOpen, activeTab, reduxDispatch]);

  useEffect(() => {
    if (isOpen && activeTab === null && !isAnswersKbEnabled) {
      reduxDispatch(setActiveTab("assistant"));
    }
  }, [isOpen, activeTab, isAnswersKbEnabled, reduxDispatch]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const maxWidth = window.innerWidth * MAX_WIDTH_RATIO;
      const newWidth = window.innerWidth - e.clientX;
      const clampedWidth = Math.max(MIN_WIDTH, Math.min(newWidth, maxWidth));
      setSidebarWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      localStorage.setItem(STORAGE_KEY, String(widthRef.current));
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing]);

  useEffect(() => {
    if (!isAssistantEnabled) return;
    if (isOpen) {
      document.body.style.marginRight = `${sidebarWidth}px`;
    } else {
      document.body.style.marginRight = "";
    }
    return () => {
      document.body.style.marginRight = "";
    };
  }, [isOpen, sidebarWidth, isAssistantEnabled]);

  if (!isAssistantEnabled) {
    return null;
  }

  const handleNewChat = () => {
    setHeaderActions(null);
    reduxDispatch(setActiveTab(null));
  };

  return (
    <div
      ref={sidebarRef}
      className={`${styles.sidebar} ${isOpen ? styles.sidebarOpen : ""}`}
      style={{ "--sidebar-width": `${sidebarWidth}px` } as React.CSSProperties}
      role="complementary"
      aria-label="Ask Gaia"
    >
      {isOpen && (
        <div
          className={`${styles.resizeHandle} ${isResizing ? styles.resizing : ""}`}
          onMouseDown={(e) => {
            e.preventDefault();
            setIsResizing(true);
          }}
        >
          <Icon name="drag-indicator" size="small" />
        </div>
      )}
      <div className={styles.header}>
        <span className={styles.headerTitle}>Ask Gaia</span>
        <SpaceBetween direction="horizontal" size="xs">
          {headerActions}
          {activeTab !== null && (
            <Button
              variant="icon"
              iconName="add-plus"
              onClick={handleNewChat}
              ariaLabel="New chat"
            />
          )}
          <Button
            variant="icon"
            iconName="angle-right"
            onClick={() => reduxDispatch(setAssistantOpen(false))}
            ariaLabel="Close sidebar"
          />
        </SpaceBetween>
      </div>
      <div className={styles.content}>
        {activeTab === null && (
          <SelectionLanding
            onSelect={(tab) => reduxDispatch(setActiveTab(tab))}
            isAnswersKbEnabled={isAnswersKbEnabled}
          />
        )}
        {activeTab === "assistant" && (
          <AssistantChatContent onHeaderActions={setHeaderActions} />
        )}
        {activeTab === "answers" && isAnswersKbEnabled && (
          <AnswersChatContent onHeaderActions={setHeaderActions} />
        )}
      </div>
    </div>
  );
};

export default SidebarChatbotDrawer;
